import {
  INCIDENT_CONSEQUENCE_VALUES,
  INCIDENT_DEADLINE_TRIGGER_VALUES,
  INCIDENT_NATURE_VALUES,
  INCIDENT_PERSON_ROLE_VALUES,
  INCIDENT_SEVERITY_VALUES,
  INCIDENT_STATUS_VALUES,
  INCIDENT_TERMINAL_STATUSES,
  INCIDENT_WORK_RELATION_VALUES,
  IncidentNature,
} from '@geartracker/types';

import { createZodDto } from './zod';

import { z } from 'zod';

const nature = z.enum(INCIDENT_NATURE_VALUES);
const consequence = z.enum(INCIDENT_CONSEQUENCE_VALUES);
const severity = z.enum(INCIDENT_SEVERITY_VALUES);

/**
 * What the declarant sends. Everything here lands in the immutable declaration
 * block, so there is no update counterpart: a declaration is never edited.
 *
 * No field carries a default value on purpose — a pre-filled qualification
 * invites a hurried submit without reading. `occurredAt` is the one exception
 * the UI pre-fills, and it does so client-side so the value is visible and
 * checkable before sending.
 */
export const DeclareIncidentSchema = z
  .object({
    occurredAt: z.coerce.date(),
    declaredNature: nature,
    declaredConsequences: consequence.array().default([]),
    declaredSeverity: severity,
    description: z.string().trim().min(1).max(10000),
    locationLabel: z.string().trim().max(300).nullable().optional(),
    folderId: z.coerce.number().int().nullable().optional(),
    equipmentIds: z.coerce.number().int().array().default([]),
    isAnonymous: z.coerce.boolean().default(false),
    declarantContact: z.string().trim().max(200).nullable().optional(),
  })
  .refine((data) => data.occurredAt <= new Date(), {
    message: 'Incident date cannot be in the future',
    path: ['occurredAt'],
  })
  // The two axes are orthogonal but not independent: consequences only exist
  // once something actually happened. Letting a "dangerous situation" carry an
  // injury would make the qualification meaningless downstream.
  .refine(
    (data) =>
      data.declaredNature === IncidentNature.DAMAGE
        ? data.declaredConsequences.length > 0
        : data.declaredConsequences.length === 0,
    {
      message:
        'Consequences are required when damage is declared, and forbidden otherwise',
      path: ['declaredConsequences'],
    },
  )
  // Keeping a way to reach the declarant is the whole point of the field; on a
  // named declaration the account already carries it.
  .refine((data) => data.isAnonymous || !data.declarantContact, {
    message: 'A contact detail is only accepted on an anonymous declaration',
    path: ['declarantContact'],
  });

/**
 * The treatment block. Deliberately a closed list: any declaration field
 * present in the payload is rejected by Zod's strict object rather than
 * silently ignored, so a client bug can never rewrite the record's
 * probative half.
 */
export const UpdateIncidentSchema = z
  .object({
    nature: nature.optional(),
    consequences: consequence.array().optional(),
    actualSeverity: severity.nullable().optional(),
    potentialSeverity: severity.nullable().optional(),
    workRelation: z.enum(INCIDENT_WORK_RELATION_VALUES).nullable().optional(),
    hasWorkStoppage: z.boolean().nullable().optional(),
    immediateActions: z.string().trim().max(20000).nullable().optional(),
    factsTimeline: z.string().trim().max(20000).nullable().optional(),
    testimonies: z.string().trim().max(20000).nullable().optional(),
    evidence: z.string().trim().max(20000).nullable().optional(),
    equipmentIds: z.coerce.number().int().array().optional(),
  })
  .strict();

export const CloseIncidentSchema = z.object({
  status: z.enum(INCIDENT_TERMINAL_STATUSES),
  closingComment: z.string().trim().max(5000).nullable().optional(),
});

export const IncidentListQuerySchema = z.object({
  skip: z.coerce.number().int().min(0).default(0),
  take: z.coerce.number().int().min(1).max(500).default(100),
  status: z.enum(INCIDENT_STATUS_VALUES).optional(),
  nature: nature.optional(),
  consequence: consequence.optional(),
  severity: severity.optional(),
  folderId: z.coerce.number().int().optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  /** Free-text search on the reference and the description. */
  search: z.string().trim().max(200).optional(),
});

/**
 * Either an app user or a free-text external person — never both, never
 * neither. Temp workers, contractors' employees and visitors have no account,
 * and they are the majority of the people involved on a shared site.
 */
export const AddIncidentPersonSchema = z
  .object({
    role: z.enum(INCIDENT_PERSON_ROLE_VALUES),
    userId: z.coerce.number().int().nullable().optional(),
    externalName: z.string().trim().max(200).nullable().optional(),
    externalCompany: z.string().trim().max(200).nullable().optional(),
    externalContact: z.string().trim().max(200).nullable().optional(),
  })
  .refine((data) => !!data.userId !== !!data.externalName?.trim(), {
    message:
      'Provide either an app user or an external person name, but not both',
    path: ['externalName'],
  })
  .refine((data) => !data.userId || !data.externalCompany, {
    message: 'An app user carries their own organisation',
    path: ['externalCompany'],
  });

export const CreateIncidentActionSchema = z.object({
  label: z.string().trim().min(1).max(300),
  dueDate: z.coerce.date().nullable().optional(),
});

export const UpdateIncidentActionSchema = z
  .object({
    label: z.string().trim().min(1).max(300).optional(),
    dueDate: z.coerce.date().nullable().optional(),
    // The client sends the intent ("done" / "not done"), never a timestamp:
    // completion time belongs to the server.
    isCompleted: z.boolean().optional(),
    completionNote: z.string().trim().max(5000).nullable().optional(),
  })
  .strict();

/**
 * No name: a poster is its scope. The server labels it after the folder it
 * covers — or after the organisation, for the site-wide one — the way
 * `listActive` already labels the organisation-wide poster it mints.
 */
export const CreateIncidentQrCodeSchema = z.object({
  folderId: z.coerce.number().int().nullable().optional(),
});

export const IncidentQrTokenSchema = z.object({
  token: z.string().uuid(),
});

/**
 * The organisation's reporting duty after an accident. All four fields are
 * always sent: the screen is one form with one save, and a partial payload
 * could not express "clear the authority" or "there is no deadline".
 *
 * `null` deadlines mean nothing to report here — the answer for a country we
 * ship no preset for, and the state every organisation starts in. `0` means at
 * once, which several regulators require (a phone call, no grace period), so
 * it is a legitimate value, not an empty one. The year-long ceiling is a typo
 * guard, nothing more.
 *
 * The trigger is never null: it says what the hours are counted from, and an
 * organisation with no deadline simply has nothing for it to qualify.
 */
export const UpdateIncidentSettingsSchema = z
  .object({
    incidentDeclarationDeadlineHours: z.coerce
      .number()
      .int()
      .min(0)
      .max(8760)
      .nullable(),
    incidentFatalDeadlineHours: z.coerce
      .number()
      .int()
      .min(0)
      .max(8760)
      .nullable(),
    incidentDeadlineTrigger: z.enum(INCIDENT_DEADLINE_TRIGGER_VALUES),
    incidentDeclarationAuthority: z
      .string()
      .trim()
      .max(200)
      .nullable()
      .transform((value) => value || null),
  })
  .strict();

export class DeclareIncidentDto extends createZodDto(DeclareIncidentSchema) {}
export class UpdateIncidentDto extends createZodDto(UpdateIncidentSchema) {}
export class CloseIncidentDto extends createZodDto(CloseIncidentSchema) {}
export class IncidentListQueryDto extends createZodDto(
  IncidentListQuerySchema,
) {}
export class AddIncidentPersonDto extends createZodDto(
  AddIncidentPersonSchema,
) {}
/* Editing replaces the whole identity rather than patching a field: the two
   halves — an app user, or a name plus their employer — exclude each other, so
   a payload that changed only the role would still have to say which half it
   keeps. Same schema, same invariants. */
export class UpdateIncidentPersonDto extends createZodDto(
  AddIncidentPersonSchema,
) {}
export class CreateIncidentActionDto extends createZodDto(
  CreateIncidentActionSchema,
) {}
export class UpdateIncidentActionDto extends createZodDto(
  UpdateIncidentActionSchema,
) {}
export class CreateIncidentQrCodeDto extends createZodDto(
  CreateIncidentQrCodeSchema,
) {}
export class IncidentQrTokenDto extends createZodDto(IncidentQrTokenSchema) {}
export class UpdateIncidentSettingsDto extends createZodDto(
  UpdateIncidentSettingsSchema,
) {}
